import React, { useState, useEffect } from 'react';
import { Container, Spinner, Modal, Button, Row, Col } from 'react-bootstrap';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { getAllRoles, addNewRole, getRoleHistory, getAvailableMenus } from '../../services/role-service';
import { refreshUserData } from '../../services/auth-service';
import InputBox from '../common/InputBox';
import RoleForm from './RoleForm';
import ResponsiveTable from '../Grid/ResponsiveTable';
import constants from '../../helpers/constants';
import { formatDate } from '../../helpers/dateUtils';

const RoleMaster = () => {
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [viewingRole, setViewingRole] = useState(null);

  const [processedHistoryData, setProcessedHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [currentHistoryPage, setCurrentHistoryPage] = useState(1);
  const historyPageSize = 10;
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [action, setAction] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingRoleUpdate, setPendingRoleUpdate] = useState(null);

  const [writeAccess, setWriteAccess] = useState(false);
  const [menuSequences, setMenuSequences] = useState({});

  const formatAuthorizedSessions = (authorizedSessions) => {
    let sessionArr = [];
    for (let key in authorizedSessions) {
      if (authorizedSessions[key]) {
        for (let menu of authorizedSessions[key]) {
          if (menu.read) {
            sessionArr.push(menu.submenu);
          }
        }
      }
    }
    return sessionArr.join(', ');
  };

  const renderMultilineText = (value) => {
    if (!value) return '';
    const lines = value.split('\n');
    return (
      <div>
        {lines.map((line, index) => (
          <div key={index}>{line}</div>
        ))}
      </div>
    );
  };

  const customCellRenderer = (value, header, item) => {
    const isChanged = item._changes && item._changes[header.columnName];
    const menuColumns = ['menu', 'submenu', 'read', 'write'];
    
    // For menu-related columns, handle line-by-line highlighting
    if (menuColumns.includes(header.columnName) && item._itemChanges && typeof value === 'string') {
      const lines = value.split('\n');
      const itemChanges = item._itemChanges[header.columnName] || [];
      
      return (
        <div>
          {lines.map((line, index) => (
            <div key={index} className={itemChanges[index] ? "history-changed" : ""}>
              {line}
            </div>
          ))}
        </div>
      );
    }
    
    // For other fields, render with highlighting if changed
    return (
      <span className={isChanged ? "history-changed" : ""}>
        {value}
      </span>
    );
  };





  const [historyData, setHistoryData] = useState([]);

  const [headers] = useState([
    { displayName: "Status", columnName: "status", dataType: "text", switch: true, setStatus: setStatus, width: 120, displayIndication: true, status: true },
    { displayName: "Role Code", columnName: "roleCode", dataType: "text", width: 200, displayIndication: true },
    { displayName: "Role Name", columnName: "roleName", dataType: "text", width: 250, displayIndication: true },
    { displayName: "Role Authorization", columnName: "authorizedSessions", dataType: "text", width: 500, displayIndication: true, formatter: formatAuthorizedSessions },
    { displayName: "Created By", columnName: "createdBy", dataType: "text", width: 150, displayIndication: true },
    { displayName: "Created On", columnName: "createdDate", dataType: "date", width: 180, displayIndication: true, formatter: formatDate },
    { displayName: "Modified By", columnName: "modifiedBy", dataType: "text", width: 150, displayIndication: true },
    { displayName: "Modified On", columnName: "modifiedDate", dataType: "date", width: 180, displayIndication: true, formatter: formatDate },
  ]);

  useEffect(() => {
    if (reload > 0) {
      loadRoles();
    }
  }, [reload]);

  useEffect(() => { // NOSONAR
    loadRoles();
    loadMenuSequences();
    // Check write access for buttons
    let AuthorizedSessionstmp = sessionStorage.getItem("AuthorizedSessions");
    if (!AuthorizedSessionstmp || AuthorizedSessionstmp === "") {
      setWriteAccess(false);
      return;
    }
    try {
      let AuthorizedSessions = JSON.parse(AuthorizedSessionstmp);
      let hasAccess = false;
      for (let parentMenu in AuthorizedSessions) {
        if (AuthorizedSessions[parentMenu]) {
          for (let menu of AuthorizedSessions[parentMenu]) {
            if (menu.submenu === "Role and Authorization" && menu.write === true) {
              hasAccess = true;
              break;
            }
          }
        }
        if (hasAccess) break;
      }
      setWriteAccess(hasAccess);
    } catch (error) {
      console.error('Error parsing AuthorizedSessions:', error);
      setWriteAccess(false);
    }
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await getAllRoles();
      setRoles(response.data || []);
    } catch (error) {
      console.error('Error loading roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const loadMenuSequences = async () => {
    try {
      const response = await getAvailableMenus();
      if (response.statusCode === '0000') {
        setMenuSequences(response.data);
      }
    } catch (error) {
      console.error('Error loading menu sequences:', error);
    }
  };

  const handleAdd = () => {
    setAction('Add');
    setEditingRole(null);
    setShowModal(true);
  };

  const handleEdit = (role) => {
    setAction('Edit');
    setEditingRole(role);
    setShowModal(true);
  };

  const handleRowClick = (role) => {
    setViewingRole(role);
    setShowViewModal(true);
  };

  function setStatus(item) {
    const updatedRole = {
      ...item,
      status: item.status === 'Active' ? 'Inactive' : 'Active'
    };
    
    // Check if role is being deactivated
    if (item.status === 'Active' && updatedRole.status === 'Inactive') {
      setPendingRoleUpdate(updatedRole);
      setShowConfirmation(true);
    } else {
      executeRoleUpdate(updatedRole);
    }
  }

  const executeRoleUpdate = async (updatedRole) => {
    setLoading(true);
    try {
      await addNewRole(updatedRole);
      toast.success('Status updated successfully');
      
      // Check if current user's role was updated
      const currentUser = JSON.parse(sessionStorage.getItem('userData') || '{}');
      if (currentUser.role === updatedRole.roleCode) {
        // Refresh current user's data and trigger refresh
        await refreshUserData();
        window.dispatchEvent(new CustomEvent('roleUpdated'));
        // Force page reload to refresh all menus
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setReload(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      const errorMsg = typeof error === 'string' ? error : error?.msg || error?.statusMsg || 'Failed to update status';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }

  const handleConfirmDeactivation = () => {
    setShowConfirmation(false);
    if (pendingRoleUpdate) {
      executeRoleUpdate(pendingRoleUpdate);
      setPendingRoleUpdate(null);
    }
  };

  const handleCancelDeactivation = () => {
    setShowConfirmation(false);
    setPendingRoleUpdate(null);
  };

  const viewHistory = async (role) => {
    setShowHistory(true);
    setHistoryLoading(true);
    setCurrentHistoryPage(1);
    try {
      const response = await getRoleHistory(role.roleCode);
      const rawData = response.data || [];
      console.log('Raw history data:', rawData);
      setHistoryData(rawData);
      
      // Process history data to group by timestamp like GiftVoucher
      const processedData = processRoleHistory(rawData);
      console.log('Processed history data:', processedData);
      setProcessedHistoryData(processedData);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const processRoleHistory = (historyData) => { // NOSONAR
    if (!historyData || historyData.length === 0 || Object.keys(menuSequences).length === 0) return [];
    
    const processed = [];
    
    // Sort by modifiedDate (newest first)
    const sortedHistory = [...historyData].sort((a, b) => 
      new Date(b.modifiedDate) - new Date(a.modifiedDate)
    );
    
    // Process each version and detect changes
    for (let i = 0; i < sortedHistory.length; i++) {
      const current = sortedHistory[i];
      const previous = sortedHistory[i + 1];
      
      if (!current.authorizedSessions) continue;
      
      const menuArray = [];
      const subMenuArray = [];
      const readArray = [];
      const writeArray = [];
      const menuChanges = [];
      const subMenuChanges = [];
      const readChanges = [];
      const writeChanges = [];
      
      let lastMenu = '';
      
      // Use menuSequences order
      for (const menu of Object.keys(menuSequences)) {
        const submenus = current.authorizedSessions[menu];
        if (!submenus || submenus.length === 0) continue;
        
        for (const submenuItem of submenus) {
          // Show menu name only when it changes (stacking display)
          if (menu !== lastMenu) {
            menuArray.push(menu);
            lastMenu = menu;
          } else {
            menuArray.push('\u00A0'); // Non-breaking space for empty cells
          }
          
          subMenuArray.push(submenuItem.submenu);
          readArray.push(submenuItem.read ? "Yes" : "No");
          writeArray.push(submenuItem.write ? "Yes" : "No");
          
          // Check what changed
          if (previous && previous.authorizedSessions && previous.authorizedSessions[menu]) {
            const prevSubmenu = previous.authorizedSessions[menu].find(s => s.submenu === submenuItem.submenu);
            if (prevSubmenu) {
              readChanges.push(submenuItem.read !== prevSubmenu.read);
              writeChanges.push(submenuItem.write !== prevSubmenu.write);
            } else {
              readChanges.push(false);
              writeChanges.push(false);
            }
          } else {
            readChanges.push(false);
            writeChanges.push(false);
          }
          menuChanges.push(false);
          subMenuChanges.push(false);
        }
      }
      
      // Determine what changed at role level
      const roleChanges = {};
      if (previous) {
        if (current.roleCode !== previous.roleCode) roleChanges.roleCode = true;
        if (current.roleName !== previous.roleName) roleChanges.roleName = true;
        if (current.status !== previous.status) roleChanges.status = true;
      }
      
      processed.push({
        roleCode: current.roleCode,
        roleName: current.roleName,
        status: current.status,
        menu: menuArray.join('\n'),
        submenu: subMenuArray.join('\n'),
        read: readArray.join('\n'),
        write: writeArray.join('\n'),
        modifiedBy: current.modifiedBy,
        modifiedDate: current.modifiedDate,
        _changes: {
          ...roleChanges,
          menu: menuChanges.some(c => c),
          submenu: subMenuChanges.some(c => c),
          read: readChanges.some(c => c),
          write: writeChanges.some(c => c)
        },
        _itemChanges: {
          menu: menuChanges,
          submenu: subMenuChanges,
          read: readChanges,
          write: writeChanges
        }
      });
    }
    
    return processed;
  };

  const icons = (item) => {
    const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
    const currentRole = userData.roleCode || userData.role || '';
    const isSysadminItem = item.roleCode === 'SYSADMIN';
    const canEditThis = writeAccess && (currentRole === 'SYSADMIN' || !isSysadminItem);
    return [
      { 
        type: 'button',
        text: 'Edit',
        click: (item) => canEditThis ? handleEdit(item) : null,
        disabled: !canEditThis
      }, 
      { 
        type: 'button',
        text: 'History',
        click: (item) => canEditThis ? viewHistory(item) : null,
        disabled: !canEditThis
      }
    ];
  };

  return (
    <>
      {loading && (
        <div className="spinner-box">
          <Spinner animation="border" className="spinner" />
        </div>
      )}

      {showModal && (
        <RoleForm
          show={showModal}
          action={action}
          rowdata={editingRole}
          setReload={() => setReload(!reload)}
          onHide={() => setShowModal(false)}
        />
      )}

      <Container bsPrefix="main-body">
        <div className="page-title">Role & Authorization Master</div>
        <div className="page-sub-title">Add and Edit Role Details</div>
        <ResponsiveTable
          headers={headers}
          data={roles}
          icons={icons}
          dataType="Roles"
          button="Role"
          handleAdd={handleAdd}
          sessionName="Role and Authorization"
          noCheckbox={true}
          pageSize={10}
          onRowClick={handleRowClick}
          enableDownload={true}
        />
      </Container>

      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>View Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={12}>
              <InputBox
                type="text"
                label="Role Code"
                name="roleCode"
                value={viewingRole?.roleCode || ''}
                readOnly={true}
              />
            </Col>
            <Col md={12}>
              <InputBox
                type="text"
                label="Role Name"
                name="roleName"
                value={viewingRole?.roleName || ''}
                readOnly={true}
              />
            </Col>
            <Col md={12}>
              <InputBox
                type="text"
                label="Status"
                name="status"
                value={viewingRole?.status || ''}
                readOnly={true}
              />
            </Col>
          </Row>
          <div className="tabledata-format mt-3">
            <Row className="text-size mb-2">
              <Col lg={6} md={6} xs={9} className="menuhead-col">
                <strong>Sessions</strong>
              </Col>
              <Col lg={6} md={6} xs={3} className="menuhead-col text-center">
                <strong>Editable</strong>
              </Col>
            </Row>
            {viewingRole?.authorizedSessions && Object.keys(menuSequences).length > 0 && (() => {
              // Use menuSequences order
              return Object.keys(menuSequences).map((menu, parentIndex) => {
                const submenus = viewingRole.authorizedSessions[menu];
                if (!submenus || submenus.length === 0) return null;
                return (
                  <React.Fragment key={parentIndex}>
                    <Row className="parentmenu-row mb-2">
                      <Col lg={12} md={12} xs={12} className="menuhead-col">
                        <strong>{menu}</strong>
                      </Col>
                    </Row>
                    {submenus.map((item, index) => (
                      <Row key={index} className="submenu-row mb-1">
                        <Col lg={6} md={6} xs={9} className="menuhead-col submenuhead-col">
                          {item.submenu}
                        </Col>
                        <Col lg={6} md={6} xs={3} className="text-center">
                          {item.write ? 'Yes' : 'No'}
                        </Col>
                      </Row>
                    ))}
                  </React.Fragment>
                );
              });
            })()}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showHistory} onHide={() => setShowHistory(false)} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Role History</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflowX: 'hidden', maxHeight: '60vh', overflowY: 'auto', padding: '1rem' }}>
          {historyLoading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="hide-pagination">
              <ResponsiveTable
                headers={[
                  { displayName: "Role Code", columnName: "roleCode", dataType: "text", width: 120, displayIndication: true, cssFormatter: customCellRenderer },
                  { displayName: "Role Name", columnName: "roleName", dataType: "text", width: 150, displayIndication: true, cssFormatter: customCellRenderer },
                  { displayName: "Status", columnName: "status", dataType: "text", width: 100, displayIndication: true, status: true, cssFormatter: customCellRenderer },
                  { displayName: "Menu", columnName: "menu", dataType: "text", width: 120, displayIndication: true, cssFormatter: customCellRenderer },
                  { displayName: "Sub Menu", columnName: "submenu", dataType: "text", width: 180, displayIndication: true, cssFormatter: customCellRenderer },
                  { displayName: "Read", columnName: "read", dataType: "text", width: 80, displayIndication: true, cssFormatter: customCellRenderer },
                  { displayName: "Write", columnName: "write", dataType: "text", width: 80, displayIndication: true, cssFormatter: customCellRenderer },
                  { displayName: "Modified By", columnName: "modifiedBy", dataType: "text", width: 150, displayIndication: true },
                  { displayName: "Modified On", columnName: "modifiedDate", dataType: "date", width: 180, displayIndication: true, formatter: formatDate },
                ]}
                data={processedHistoryData.slice((currentHistoryPage - 1) * historyPageSize, currentHistoryPage * historyPageSize)}
                dataType="RoleHistory"
                noCheckbox={true}
                pageSize={historyPageSize}
              />
            </div>
          )}
          <style>{`
            .hide-pagination .pagination-container {
              display: none !important;
            }
            #RoleHistory_table tbody td {
              vertical-align: top !important;
            }
          `}</style>
        </Modal.Body>
        <div style={{ borderTop: '1px solid #F1F1ED', padding: '1% 2%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F9F2F6' }}>
          <span style={{ fontSize: '14px' }}>{(currentHistoryPage - 1) * historyPageSize + 1}-{Math.min(currentHistoryPage * historyPageSize, processedHistoryData.length)} of {processedHistoryData.length} items</span>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <ChevronLeft
              size={18}
              onClick={() => currentHistoryPage > 1 && setCurrentHistoryPage(currentHistoryPage - 1)}
              style={{ cursor: currentHistoryPage === 1 ? 'default' : 'pointer', color: currentHistoryPage === 1 ? '#b5b9c6' : '#333333' }}
            />
            <span>Page {currentHistoryPage} of {Math.ceil(processedHistoryData.length / historyPageSize) || 1}</span>
            <ChevronRight
              size={18}
              onClick={() => currentHistoryPage < Math.ceil(processedHistoryData.length / historyPageSize) && setCurrentHistoryPage(currentHistoryPage + 1)}
              style={{ cursor: currentHistoryPage === Math.ceil(processedHistoryData.length / historyPageSize) ? 'default' : 'pointer', color: currentHistoryPage === Math.ceil(processedHistoryData.length / historyPageSize) ? '#b5b9c6' : '#333333' }}
            />
          </div>
        </div>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistory(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Confirmation Modal */}
      <Modal show={showConfirmation} onHide={handleCancelDeactivation} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Role Deactivation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          All the users associated with this role will be deactivated. Do you wish to continue?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDeactivation}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmDeactivation}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RoleMaster;
